import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface CheckboxConfigModel {
  label: string;
  helpText: string;
  defaultValue: boolean;
  linkText: string;
  linkUrl: string;
  required: boolean;
}

@Component({
  selector: 'app-checkbox-config-view',
  standalone: true,
  imports: [SignalFormField, SignalTextField, SignalCheckbox, SignalFormDirective],
  templateUrl: './checkbox-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<CheckboxConfigModel>({
    label: '',
    helpText: '',
    linkText: '',
    linkUrl: '',
    defaultValue: false,
    required: false,
  });
  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.type !== 'checkbox') {
        return;
      }
      const config = next.config;
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        linkText: next.checkboxLinkText ?? '',
        linkUrl: next.checkboxLinkUrl ?? '',
        defaultValue:
          typeof next.config?.defaultValue === 'boolean'
            ? next.config.defaultValue
            : false,
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        checkboxLinkText: draft.linkText.trim() || undefined,
        checkboxLinkUrl: draft.linkUrl.trim() || undefined,
        config: {
          ...field.config,
          defaultValue: draft.defaultValue,
        },
        validation: {
          required: draft.required,
        },
        required: draft.required,
        options: undefined,
      });
    });
  }
}
